import React from 'react';
import moment from 'moment';
import { IntlProvider } from 'react-intl';
import languages from '../languages/languages';
import defaults from '../i18n-defaults.json';
import 'moment/dist/locale/es';
import 'moment/dist/locale/fr';
import 'moment/dist/locale/fr-ca';
import 'moment/dist/locale/zh-cn';
import 'moment/dist/locale/pt-br';
import 'moment/dist/locale/de';
import 'moment/dist/locale/ru';
import 'moment/dist/locale/zh-tw';
import 'moment/dist/locale/tr';
import 'moment/dist/locale/ko';
import 'moment/dist/locale/ja';
import 'moment/dist/locale/ro';
import 'moment/dist/locale/sv';
import 'moment/dist/locale/hu';
import 'moment/dist/locale/cs';
import 'moment/dist/locale/pl';
import 'moment/dist/locale/nl';
import 'moment/dist/locale/it';
import 'moment/dist/locale/hr';

type TranslationObjectType = { [key: string]: string };
// when custom messages come from outside we want to use only valid messages, to not override default messages that has value
// in order to provide a fallback in the same language.
export const getObjectWithOnlyKeysThatHaveNonEmptyValues = (
  translationsObject: TranslationObjectType,
): TranslationObjectType => {
  const objectWithNonEmptyValues: Record<string, string> = {};

  Object.keys(translationsObject || {}).forEach(key => {
    if (translationsObject[key]) {
      objectWithNonEmptyValues[key] = translationsObject[key];
    }
  });

  return objectWithNonEmptyValues;
};

const I18nProvider = ({
  children,
  messages = defaults,
  language,
  version,
  onError,
  shouldLoadExternalTranslations,
}: {
  children: React.ReactNode;
  messages?: TranslationObjectType;
  language: string;
  version?: string;
  shouldLoadExternalTranslations?: boolean;
  onError?: (errorString: Error | string, extra?: Record<string, unknown>) => void;
}): React.JSX.Element => {
  const [i18nTexts, setI18nTexts] = React.useState(messages);

  React.useEffect(() => {
    moment?.locale?.((language || 'en').toLowerCase?.());
  }, [language]);

  React.useEffect(() => {
    let translationFile = {};
    let translationDynamicModules;

    if (version && language !== languages.en.value && shouldLoadExternalTranslations) {
      // load translation file from CDN
      (async () => {
        try {
          const externalFileResponse = await fetch(
            `${localStorage.getItem('apiHost') || ''}/translation/${version}/${language}.json`,
            {
              cache: 'no-store',
            },
          );

          if (!externalFileResponse.ok) {
            throw new Error('Failed to fetch external translation file');
          }
          translationFile = await externalFileResponse.json();
          setI18nTexts({ ...i18nTexts, ...translationFile });
        } catch {
          onError?.('Could not load external locale file - try loading bundle', {
            extra: { language, version },
          });
          try {
            translationDynamicModules = import.meta.glob(`../../translations/*.json`) || {};
            const module = await translationDynamicModules?.[`../../translations/${language}.json`]?.();
            const finalTranslationFile = module || translationFile;
            setI18nTexts({ ...i18nTexts, ...finalTranslationFile });
          } catch {
            setI18nTexts({ ...i18nTexts, ...defaults });
          }
        }
      })();
    } else {
      // load translation file from bundle
      (async () => {
        let translationDynamicModules;
        try {
          // there is no en.json on translation folder, no need to attempt import
          if (language !== languages.en.value) {
            translationDynamicModules = import.meta.glob(`../../translations/*.json`) || {};
            const module = await translationDynamicModules?.[`../../translations/${language}.json`]?.();
            const finalTranslationFile = module || translationFile;
            setI18nTexts({ ...i18nTexts, ...finalTranslationFile });
          } else {
            setI18nTexts({ ...i18nTexts, ...defaults });
          }
        } catch {
          console.warn('Failed to import async translations');
        }
      })();
    }
  }, [language]);

  React.useEffect(() => {
    document.documentElement.setAttribute('lang', languages?.[language]?.langAttribute || language);
  }, [language]);

  const textsToRender = { ...i18nTexts, ...getObjectWithOnlyKeysThatHaveNonEmptyValues(messages) };

  return (
    <IntlProvider
      onError={err => {
        // https://github.com/formatjs/formatjs/issues/465#issuecomment-643887472
        // canceling error for missing translation. we don't need them to be logged.
        if (err.code === 'MISSING_TRANSLATION') {
          return;
        }
        console.warn(err.code, err.message);
      }}
      key={language}
      locale={language}
      messages={textsToRender}
    >
      {children}
    </IntlProvider>
  );
};
export default I18nProvider;
